/* eslint-disable arrow-body-style */
import * as React from "react";
import {autorun, reaction} from "mobx";
import {observer} from "mobx-react";
import {Loader, Message} from "semantic-ui-react";

import "./feature-constructor.scss";


import EsriGraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import EsriSketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";


import {Auftragsverwaltung} from "../../../../../stores/auftragsverwaltung-mobx";

import {MapState} from "../../../../../core/map-state";
import {UiState} from "../../../../../core/ui-state";

import {MapViewerConfiguration} from "../../../../../config/map-viewer-configuration";

import {Project} from "../../../../../models/project";
import EsriFeatureTemplates from "@arcgis/core/widgets/FeatureTemplates";
import {ProjectPerimeter} from "../../../../../models/project-perimeter";
import {
    CancelButton, DeleteButton,
    RedoButton,
    SaveButton,
    SaveUpdatesButton,
    UndoButton,
    UpdateButton
} from "./feature-constructor-tools/feature-constructor-tools";
import Graphic from "@arcgis/core/Graphic";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import {AppState} from "../../../app-mobx";
import EsriGraphic from "@arcgis/core/Graphic";
import EsriFeatureLayer from "@arcgis/core/layers/FeatureLayer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";

export enum FeatureConstructorMode {
    FeatureConstruction = "featureConstruction",
    FeatureSelection = "featureSelection",
    FeatureUpdate = "featureUpdate"
}

export interface FeatureConstructorProps {
    auftragsverwaltung: Auftragsverwaltung;
    mapViewerConfig: MapViewerConfiguration;
    project: Project | null;
    mapStore: MapState;
    appStore: AppState;
    uiStore: UiState;
}

/**
 * Create features
 */
@observer
export class FeatureConstructor extends React.Component<FeatureConstructorProps, any> {
    watchHandles: any[] = [];
    sketchWatchHandle: any;
    mapViewClickWatchHandle: any;
    mapViewMoveWatchHandle: any;
    autorunDisposers: any[] = [];

    proposalLayer: EsriGraphicsLayer;
    highlightLayer: EsriGraphicsLayer;

    sketchViewModel: EsriSketchViewModel;
    featureTemplates: EsriFeatureTemplates;
    featureTemplatesRef: any;

    constructor(props: FeatureConstructorProps) {
        super(props);
        this.state = {
            featureTemplatesLoaded: false,
            mode: FeatureConstructorMode.FeatureConstruction,
            selectedTemplate: null,  // the currently selected feature template
            perimeterProposal: null, // proposal for a new polygon feature
            updateFeature: null,
            updateState: null  // use sketchViewModel updateState to force rerender (to enable/disable buttons)
        };

        this.featureTemplatesRef = React.createRef();

        this.proposalLayer = new EsriGraphicsLayer({listMode: "hide", title: "proposalLayer 1", opacity: 0.7 });
        this.highlightLayer = new EsriGraphicsLayer({listMode: "hide", title: "highlightLayer 1"});

        this.handleSketchCreate = this.handleSketchCreate.bind(this);
        this.handleTemplateSelect = this.handleTemplateSelect.bind(this);
        this.handleSelectFeature = this.handleSelectFeature.bind(this);
        this.handleHighlightFeature = this.handleHighlightFeature.bind(this);
    }

    private get hasProposal(): boolean {
        const {pointProposal, lineProposal, perimeterProposal} = this.state;
        return pointProposal || lineProposal || perimeterProposal;
    }

    componentDidMount(): void {
        const {auftragsverwaltung, mapStore} = this.props;

        // rerun until map view is ready and layer list view model is loaded
        this.autorunDisposers.push(autorun((r: any) => {
            if (!auftragsverwaltung.selectedProject || !auftragsverwaltung.apiLoaded) {
                return;
            }

            if (!mapStore.map || !mapStore.view) {
                return;
            }
            r.dispose();

            // add proposal layer to the map
            if (this.proposalLayer && !mapStore?.map.layers.includes(this.proposalLayer)) {
                mapStore?.map.add(this.proposalLayer);
            }
            // add highlight layer
            if (this.highlightLayer && !mapStore?.map.layers.includes(this.highlightLayer)) {
                mapStore?.map.add(this.highlightLayer);
            }

            this.clearHighlights();

            this.initFeatureTemplates();
            this.initSketchViewModel();
        }));
        // watch changes in editing mode
        this.autorunDisposers.push(
            reaction(
                () => auftragsverwaltung.isEditingPerimeter,
                (isEditingProject) => {
                    if (!isEditingProject) {
                        this.clearHighlights();
                    }
                }
            )
        );
    }

    componentDidUpdate() {}


    componentWillUnmount(): void {
        const {auftragsverwaltung} = this.props;

        this.clearHighlights();
        this.deactivateFeatureSelector();
        this.cancelFeatureUpdate();

        // hide parcel layer
        const parcelLayer = auftragsverwaltung.ParcelStore?.viewLayer;
        if (parcelLayer) {
            parcelLayer.visible = false;
        }

        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;

        this.autorunDisposers.forEach((dispose: any) => {
            dispose();
        });
        this.autorunDisposers.length = 0;

        this.sketchViewModel?.destroy();
        this.featureTemplates?.destroy();
    }

    render() {
        const {mode, featureTemplatesLoaded, updateFeature, updateState} = this.state;

        return (
            <div className="feature-constructor">
                {!updateFeature &&
                    <div className="feature-constructor-tools">
                        <Message
                            attached info size={"mini"}
                            header="A) Perimeter in Karte einzeichnen"
                            list={[
                                "Objekttyp (Vorlage) auswählen",
                                "In Karte einzeichnen",
                            ]}
                        />
                        {this.hasProposal &&
                            <>
                                <CancelButton onCancel={() => {
                                    this.cancelFeatureCreation();
                                }} />
                                <SaveButton onSave={() => {
                                    this.saveProjectFeature();
                                }} />
                            </>
                        }
                    </div>
                }
                <div className="geo-project-feature-templates-wrapper" ref={this.featureTemplatesRef} >
                    <Loader
                        active={!featureTemplatesLoaded}
                        inline="centered"
                        size="mini"
                        disabled={featureTemplatesLoaded}>
                        Objektvorlagen laden...
                    </Loader>
                </div>
                {!this.hasProposal &&
                    <div className="feature-update-tools">
                        <Message
                            attached info size={"mini"}
                            header="B) Perimeter ändern"
                            list={[
                                "Perimeter in Karte auswählen",
                                "Stützpunkte ändern oder hinzufügen"
                            ]}
                        />
                        <UpdateButton
                            active={mode === FeatureConstructorMode.FeatureSelection}
                            onUpdate={() => {
                                this.activateFeatureSelector();
                            }}
                        />
                        {updateFeature &&
                            <CancelButton onCancel={() => {
                                this.cancelFeatureUpdate();
                            }} />
                        }
                        {updateState !== "complete" && this.sketchViewModel?.canUndo() &&
                            <UndoButton
                                onUndo={() => {
                                    this.sketchViewModel?.undo();
                                }}
                            />
                        }
                        {updateState !== "complete" && this.sketchViewModel?.canRedo() &&
                            <RedoButton
                                onRedo={() => {
                                    this.sketchViewModel?.redo();
                                }}
                            />
                        }
                        {updateFeature &&
                            <DeleteButton onDelete={() => {
                                this.deleteProjectFeature(updateFeature);
                            }} />
                        }
                        {updateState !== "complete" && updateFeature && this.sketchViewModel.canUndo() &&
                            <SaveUpdatesButton onSave={() => {
                                this.updateProjectFeature();
                            }} />
                        }
                    </div>
                }
            </div>
        );
    }

    private handleTemplateSelect(evtTemplate): void {
        // const {selectedTemplate} = this.state;
        // check if template has changed
        // if (selectedTemplate?.name === evtTemplate.template.name) {
        //     return;
        // }

        this.deactivateFeatureSelector();
        this.clearUpdateFeature();

        // template changed. Any pending proposals?
        if (this.hasProposal) {
            if (!confirm("Nicht gespeicherte Änderungen gehen verloren. Vorlage wechseln?")) {
                // do not clear
                return;
            }
        }

        this.clearProposalLayer();
        this.clearProposals();
        this.clearHighlights();

        // Access the template item's attributes from the event's
        // template prototype.
        this.setState({
            mode: FeatureConstructorMode.FeatureConstruction,
            selectedTemplate: evtTemplate.template
        });

        let tool = evtTemplate.template.drawingTool;
        // convert to sketch tool
        if (tool === "line") {
            tool = "polyline";
        }

        // start sketch drawing with tool specified by template
        this.sketchViewModel.create(tool);
    }

    /**
     * Cancel the construction mode
     * @private
     */
    private cancelFeatureCreation(): void {
        this.clearProposalLayer();
        this.clearProposals();
    }

    /**
     * Cancel the construction mode
     * @private
     */
    private cancelFeatureUpdate(): void {
        this.clearProposalLayer();
        this.clearUpdateFeature();
        this.sketchViewModel.cancel();
        // show feature templates
        this.toggleFeatureTemplatesVisibility(true);
    }

    private toggleFeatureTemplatesVisibility(visible: boolean): void {
        this.featureTemplates.visible = visible;
    }

    private clearProposals(): void {
        const {auftragsverwaltung} = this.props;

        this.setState({
            pointProposal: null,
            lineProposal: null,
            perimeterProposal: null
        });
        auftragsverwaltung.ProjectPerimeterStore?.clearProposal();
    }

    private clearUpdateFeature(): void {
        const {auftragsverwaltung} = this.props;

        this.setState({
            updateFeature: null
        });
        auftragsverwaltung.ProjectPerimeterStore?.clearProposal();
    }

    /**
     * Activate mode to select a feature to edit
     * @private
     */
    private activateFeatureSelector(): void {
        const {mapStore} = this.props;

        this.setState({
            mode: FeatureConstructorMode.FeatureSelection,
            updateFeature: null
        });

        // cancel active sketch workflows
        this.sketchViewModel.cancel();
        // clear previous update proposals
        this.clearProposalLayer();

        // add click handler to the view
        this.mapViewClickWatchHandle = mapStore.view?.on("pointer-down", this.handleSelectFeature);
        // add handles to highlight features when hovering
        this.mapViewMoveWatchHandle = mapStore.view?.on("pointer-move", this.handleHighlightFeature);
    }

    private deactivateFeatureSelector(): void {
        this.mapViewClickWatchHandle?.remove();
        this.mapViewMoveWatchHandle?.remove();
    }

    /**
     * Save project feature
     * @private
     */
    private saveProjectFeature(): void {
        const {auftragsverwaltung, appStore} = this.props;
        const {perimeterProposal} = this.state;

        if (!this.hasProposal) {
            return;
        }

        const user = appStore.esriCredential?.userId || "";
        if (perimeterProposal) {
            auftragsverwaltung.ProjectPerimeterStore?.save(perimeterProposal, undefined, user).then(() => {
                this.clearProposalLayer();
                this.setState({
                    perimeterProposal: null
                });
                auftragsverwaltung.ProjectPerimeterStore?.clearProposal();
            });
        }
    }

    /**
     * Update project feature
     * @private
     */
    private updateProjectFeature(): void {
        // end sketch update mode.
        // This changes update event state to complete.
        this.sketchViewModel.complete();
        this.clearProposalLayer();
        // show feature templates
        this.toggleFeatureTemplatesVisibility(true);
    }

    private deleteProjectFeature(updateFeature: EsriGraphic): void {
        const layer = updateFeature.layer as EsriFeatureLayer;
        layer.applyEdits({
            deleteFeatures: [updateFeature]
        });
        this.clearUpdateFeature();
        this.sketchViewModel.cancel();
        this.clearProposalLayer();
        this.toggleFeatureTemplatesVisibility(true);
    }

    /**
     * Clear proposal layers
     * @private
     */
    private clearProposalLayer(): void {
        this.proposalLayer?.removeAll();
    }
    /**
     * Clear highlight layers
     * @private
     */
    private clearHighlights(): void {
        this.highlightLayer?.removeAll();
    }

    /**
     * Select first feature hit in the map
     * @param event __esri.ScreenPoint | MouseEvent
     * @private
     */
    private handleSelectFeature(event: __esri.ScreenPoint | MouseEvent): void {
        this.getFullResolutionFeatureByHitTest(event).then((graphic: __esri.Graphic) => {
            this.clearHighlights();
            if (!graphic) {
                return;
            }

            // do not hit other features while updating
            this.deactivateFeatureSelector();

            // clear previous update proposals
            this.clearProposalLayer();

            // hide feature templates
            this.toggleFeatureTemplatesVisibility(false);

            // take first graphic found
            this.setState({
                updateFeature: graphic
            });

            // create new graphic for updating
            const proposal = new Graphic({
                attributes: {...graphic.attributes},  // do clone! otherwise attributes are referenced and reused
                layer: this.proposalLayer,
                geometry: graphic.geometry.clone()
            });
            // add graphic to proposal layer
            this.proposalLayer.add(proposal);
            this.updateFeature(proposal);
        });
    }

    /**
     * Highlight feature when hovering
     * @param event
     * @private
     */
    private handleHighlightFeature(event: __esri.ScreenPoint | MouseEvent): void {
        // do not highlight features when a feature is in update mode
        const {updateFeature} = this.state;
        if (updateFeature) {
            return;
        }

        this.getFeatureByHitTest(event).then((graphic: __esri.Graphic) => {
            this.clearHighlights();
            if (!graphic) {
                return;
            }

            const symbol = this.getHighlightSymbol(graphic);
            if (symbol) {
                graphic.symbol = symbol;
            }
            this.highlightLayer.add(graphic);

        });
    }

    /**
     * Get geometry with full resolution by a hit test
     * Returns the first graphic found
     * @param event
     * @private
     */
    private getFullResolutionFeatureByHitTest(event: __esri.ScreenPoint | MouseEvent): Promise<Graphic | null> {
        return new Promise((resolve) => {
            this.getFeatureByHitTest(event).then((graphic: __esri.Graphic | null) => {
                if (!graphic) {
                    resolve(null);
                    return;
                }

                const layer = graphic.layer as FeatureLayer;
                if (!layer) {
                    resolve(null);
                    return;
                }

                const query = layer.createQuery();
                query.returnGeometry = true;
                query.objectIds = [graphic.attributes.OBJECTID];

                layer.queryFeatures(query).then((result) => {
                    if (result.features.length > 0) {
                        resolve(result.features[0]);
                    }
                });
            });
        });
    }

    /**
     * Find feature on selectable layers
     * @param event
     * @private
     */
    private getFeatureByHitTest(event: __esri.ScreenPoint | MouseEvent): Promise<__esri.Graphic | null> {
        return new Promise((resolve) => {
            const {auftragsverwaltung, mapStore} = this.props;

            const layers = [
                this.proposalLayer,
                auftragsverwaltung.ProjectPerimeterStore?.viewLayer,
            ];
            // screenPoint: MapViewScreenPoint | MouseEvent, options?: MapViewHitTestOptions
            mapStore.view?.hitTest(event, {include: layers}).then((response) => {
                const graphicHits = response.results?.filter(
                    (hitResult) => hitResult.type === "graphic"
                );
                if (!graphicHits || graphicHits.length === 0) {
                    resolve(null);
                    return;
                }

                // take first graphic found and request full resolution geometry
                const hitGraphic = (graphicHits[0] as __esri.GraphicHit).graphic;
                resolve(hitGraphic);
            });
        });
    }

    /**
     * Set feature to update mode
     * @param graphic
     * @private
     */
    private updateFeature(graphic: EsriGraphic): void {
        // pass the feature to update mode
        setTimeout(() => {
            this.sketchViewModel.update(
                graphic,
                {
                    tool: "reshape",
                    enableRotation: false,
                    enableScaling: false,
                    enableZ: false
                }
            );
        }, 400);
    }

    /**
     * Handles the creation of features completed by sketchViewModel
     * https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch-SketchViewModel.html#events-summary
     * @param event
     * @private
     */
    private handleSketchCreate(event: __esri.SketchViewModelCreateEvent): void {
        const {auftragsverwaltung, appStore, project} = this.props;
        const {selectedTemplate} = this.state;
        const user = appStore.esriCredential?.userId || "";

        // check if the create event's state has changed to complete indicating
        // the graphic create operation is completed.
        if (event.state === "complete") {
            const templateAttributes = selectedTemplate.prototype.attributes;
            const graphic = event.graphic;
            const geometry = graphic.geometry;

            if (!project?.id) {
                throw new Error("Project id undefined");
            }

            const store = auftragsverwaltung.ProjectPerimeterStore;
            if (!store) {
                throw new Error("Perimeter store undefined when creating feature");
            }

            // create proposal for feature based on selected tool
            // set to state to be able to save it when user presses save
            switch (event.tool) {
            case "point":
                throw new Error("Sketch type 'point' not implemented.");
            case "polyline":
                throw new Error("Sketch type 'polyline' not implemented.");
            case "polygon":
                store.newItem(project?.id, user, geometry)
                    .then((perimeter) => {
                        if (perimeter) {
                            const objectTypeFieldName = ProjectPerimeter.databaseFieldNames.get("objectType") || "";
                            perimeter.objectType = templateAttributes[objectTypeFieldName];

                            this.setState({
                                perimeterProposal: perimeter
                            });
                            this.proposalLayer.graphics.add(graphic);
                            // remember in store
                            store.proposal = perimeter;
                        }
                    });
                return;
            case "multipoint":
                throw new Error("Sketch type 'multipoint' not implemented.");
            case "rectangle":
                throw new Error("Sketch type 'rectangle' not implemented.");
            case "circle":
                throw new Error("Sketch type 'circle' not implemented.");
            }

        }
    }

    /**
     * Handles the update of features completed by SketchViewModel
     * @param event
     * @private
     */
    private handleSketchUpdate(event: __esri.SketchViewModelUpdateEvent): void {
        const {auftragsverwaltung} = this.props;
        const {updateFeature} = this.state;

        // remember sketchViewModel state to trigger rerender
        this.setState({
            updateState: event.state
        });

        if (event.aborted) {
            return;
        }

        if (event.state === "active") {
            // ongoing update process
            const store = auftragsverwaltung.ProjectPerimeterStore;
            if (!store) {
                throw new Error("Perimeter store undefined when creating feature");
            }
            // remember in store
            store.proposal = updateFeature;
        }
        // check if the update event's state has changed to complete indicating
        // the graphic update operation is completed.
        if (event.state === "complete") {
            // the updated graphic
            const graphic = event.graphics[0];
            const updatedGeometry = graphic?.geometry;
            const originalGeometry = updateFeature?.geometry;

            if (!originalGeometry || !updatedGeometry ||
                geometryEngine.equals(originalGeometry, updatedGeometry)) {
                // only save when geometry has changed
                return;
            }

            // update geometry
            updateFeature.geometry = updatedGeometry;
            updateFeature.layer.applyEdits({
                updateFeatures: [updateFeature]
            });
            this.clearUpdateFeature();
        }
    }

    private initSketchViewModel(): void {
        const {auftragsverwaltung} = this.props;

        // wait for parcel layer loaded and ready in view
        // needed for snapping
        this.autorunDisposers.push(
            autorun((viewLayerReaction) => {
                if (auftragsverwaltung.ParcelStore?.viewLayer) {
                    viewLayerReaction.dispose();
                    this.configureSketchViewModel();
                }
            })
        );
    }

    private configureSketchViewModel(): void {
        const {auftragsverwaltung, mapStore, mapViewerConfig} = this.props;

        const parcelLayer = auftragsverwaltung.ParcelStore?.viewLayer;
        parcelLayer.visible = true;

        // initialize model to draw a selection box
        this.sketchViewModel = new EsriSketchViewModel({
            view: mapStore?.view,
            layer: this.proposalLayer,
            tooltipOptions: {
                enabled: true
            },
            defaultCreateOptions: {
                mode: "hybrid"
            },
            snappingOptions: {
                enabled: true,
                // assigns a collection of FeatureSnappingLayerSource() and enables feature snapping on this layer
                featureSources: [{layer: parcelLayer, enabled: true}]
            },
            polygonSymbol: mapViewerConfig.symbols["perimeterProposal"] as __esri.SimpleFillSymbol
        });
        this.watchHandles.push(
            this.sketchViewModel.on("create", (event) => {
                this.handleSketchCreate(event);
            })
        );
        this.watchHandles.push(
            this.sketchViewModel.on("update", (event) => {
                this.handleSketchUpdate(event);
            })
        );
        // watch changes in application edit mode
        this.autorunDisposers.push(autorun((editingReaction: any) => {
            if (!auftragsverwaltung.isEditingProject) {
                editingReaction.dispose();

                this.clearProposalLayer();
                this.clearHighlights();
            }
        }));
    }

    private initFeatureTemplates(): void {
        const {auftragsverwaltung, project} = this.props;

        this.autorunDisposers.push(
            autorun((allViewLayersLoadedReaction) => {
                if (!auftragsverwaltung.ProjectPerimeterStore.viewLayer) {
                    // wait for viewLayers to be loaded
                    return;
                }
                allViewLayersLoadedReaction.dispose();

                const perimeterLayer = auftragsverwaltung.ProjectPerimeterStore.viewLayer;

                const layers = [
                    perimeterLayer
                ];

                this.featureTemplates = new EsriFeatureTemplates({
                    container: this.featureTemplatesRef.current,
                    layers,
                    visibleElements: {
                        filter: false, // disable the default filter UI
                    },
                    filterFunction: (filter): boolean => {
                        if (project?.typeId === 4) {
                            // risk project
                            return filter.label.includes("Risiko");
                        }
                        // GK, GHK or GK+GHK
                        return filter.label.includes("Gefahren");
                    }
                });

                this.featureTemplates.on("select", (evtTemplate) => {
                    this.handleTemplateSelect(evtTemplate);
                });
                this.setState({
                    featureTemplatesLoaded: true
                });
            })
        );
    }

    /**
     * Get the matching highlight symbol
     * @param graphic
     * @private
     */
    private getHighlightSymbol(graphic: __esri.Graphic): __esri.Symbol {
        const {mapViewerConfig} = this.props;
        if (graphic.geometry.type === "point") {
            return mapViewerConfig.symbols["pointHighlightSymbol"] as __esri.SimpleMarkerSymbol;
        }
        else if (graphic.geometry.type === "polyline") {
            return mapViewerConfig.symbols["polylineHighlightSymbol"] as __esri.SimpleLineSymbol;
        }
        else if (graphic.geometry.type === "polygon") {
            return mapViewerConfig.symbols["polygonHighlightSymbol"] as __esri.SimpleFillSymbol;
        }
        throw new Error(`No symbol specified for geometry type. ${graphic.geometry?.type}`);
    }
}
